import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, CreditCard, MessageSquare, TrendingUp, Loader2 } from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { toast } from 'react-hot-toast';

export default function AdminDashboard() {
  const [stats, setStats] = useState<any>(null);
  const [revenue, setRevenue] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, revenueRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getRevenueStats(),
      ]);
      setStats(statsRes.stats);
      setRevenue(revenueRes.revenue);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-navy-800">Admin Dashboard</h1>
        <p className="mt-1 text-graphite">Platform overview and management</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 rounded-lg">
              <Users className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-graphite">Total Users</p>
              <p className="text-2xl font-bold text-navy-800">{stats?.users.total || 0}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="text-green-600">+{stats?.users.new_today || 0}</span>
            <span className="text-gray-500">today</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CreditCard className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-graphite">Active Subscriptions</p>
              <p className="text-2xl font-bold text-navy-800">{stats?.subscriptions.active || 0}</p>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            MRR: <span className="font-semibold text-navy-800">{revenue?.mrr_formatted || '$0'}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <MessageSquare className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-graphite">Total Widgets</p>
              <p className="text-2xl font-bold text-navy-800">{stats?.widgets.total || 0}</p>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Active: <span className="font-semibold">{stats?.widgets.active || 0}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-aqua-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-aqua-600" />
            </div>
            <div>
              <p className="text-sm text-graphite">Messages This Month</p>
              <p className="text-2xl font-bold text-navy-800">
                {(stats?.usage.messages_this_month || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Users by Plan */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy-800 mb-4">Users by Plan</h2>
          <div className="space-y-3">
            {stats?.users.by_plan?.map((item: any) => (
              <div key={item.plan} className="flex items-center justify-between">
                <span className="capitalize text-graphite">{item.plan}</span>
                <span className="font-semibold text-navy-800">{item.count}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-graphite">Verified Users</span>
              <span className="font-semibold text-navy-800">
                {stats?.users.verified || 0} ({Math.round((stats?.users.verified / stats?.users.total) * 100) || 0}%)
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy-800 mb-4">Monthly Signups</h2>
          <div className="space-y-3">
            {revenue?.monthly_signups?.slice(0, 6).map((item: any) => (
              <div key={item.month} className="flex items-center justify-between">
                <span className="text-graphite">{item.month}</span>
                <span className="font-semibold text-navy-800">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-navy-800 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/users"
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Manage Users
          </Link>
          <Link
            to="/admin/plans"
            className="px-4 py-2 border border-gray-300 text-graphite rounded-lg hover:bg-gray-50 transition-colors"
          >
            Configure Plans
          </Link>
        </div>
      </div>
    </div>
  );
}
